import React, { useMemo, useState } from "react";
import { PDFDownloadLink } from "@react-pdf/renderer";
import DapPdf from "./DapPdf";

const DECHETS_BASE = [
  { code: "17 01 01 / 17 01 01 02", designation: "Béton Ferraillé" },
  { code: "17 01 01 / 17 01 01 01", designation: "Béton Non Ferraillé" },
  { code: "17 01 02 / 17 01 02 00", designation: "Briques" },
  { code: "17 01 07 / 17 01 07 01", designation: "Mélange Cailloux Bétons Briques sans fines (déblais routiers)" },
  { code: "17 01 07 / 17 01 07 01", designation: "Mélange inerte contenant de la fines hors terre (déblais sableux)" },
  { code: "17 05 04 / 17 05 04 02", designation: "Mélange terres et cailloux sans substances dangereuses (déblais terreux)" },
  { code: "17 03 02 / 17 03 02 00", designation: "Mélange bitumeux (rabotages, enrobés) avec analyse obligatoire" },
];

const emptyParty = () => ({
  raisonSociale: "",
  siret: "",
  adresse: "",
  cp: "",
  commune: "",
  contact: "",
  email: "",
  tel: "",
});

export default function DapForm() {
  const [data, setData] = useState({
    site: "", // "GRANDE_SYNTHE" | "LOON_PLAGE"
    chantier: { nom: "", adresse: "", cp: "", commune: "", contact: "", email: "", tel: "" },
    producteur: emptyParty(),
    demandeur: emptyParty(),
    dechets: DECHETS_BASE.map((d) => ({ ...d, oui: false, quantite: "" })),
    transporteur: emptyParty(),
    travauxParcellesBatiments: null, // true | false | null
    analyse: { fournie: null, date: "", ref: "" }, // true | false | null
    engagements: [
      "Livrer des déchets inertes conformes aux spécifications de ce document et ne pas procéder à une dilution des déchets",
      "Porter à la connaissance du site d’acceptation tout changement qui interviendrait sur les déchets modifiant ces indications",
      "Evacuer en filière agréée tous déchets qui s’avéreraient être pollués",
      "Faire analyser tout déchets inertes provenant d’un chantier de dépollution et apporter avec le présent document, les résultats prouvant le caractère inerte",
    ],
    acceptationSem: { oui: null, noCap: "", motifs: [] }, // cadre réservé SEM
  });

  const update = (path, value) => {
    setData((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let ref = next;
      while (keys.length > 1) ref = ref[keys.shift()];
      ref[keys[0]] = value;
      return next;
    });
  };

  const toggleDechet = (idx, key) => {
    setData((prev) => {
      const next = structuredClone(prev);
      next.dechets[idx][key] = !next.dechets[idx][key];
      return next;
    });
  };

  const pdfDoc = useMemo(() => <DapPdf data={data} />, [data]);

  return (
    <div style={{ maxWidth: 1000, margin: "24px auto", padding: 16, fontFamily: "system-ui, Arial" }}>
      <h1 style={{ margin: 0 }}>Demande d’acceptation préalable (DAP) — Formulaire</h1>
      <p style={{ marginTop: 6, color: "#444" }}>
        Saisie + export PDF statique (2 pages).
      </p>

      <div style={{ display: "flex", gap: 12, alignItems: "center", margin: "16px 0" }}>
        <PDFDownloadLink document={pdfDoc} fileName="DAP_SEM5962.pdf">
          {({ loading }) => (
            <button type="button" style={{ padding: "10px 14px", cursor: "pointer" }}>
              {loading ? "Génération du PDF..." : "Télécharger le PDF"}
            </button>
          )}
        </PDFDownloadLink>
      </div>

      <fieldset style={{ border: "1px solid #ddd", padding: 12 }}>
        <legend>En-tête</legend>
        <label>Site de mise en décharge : </label>
        <select value={data.site} onChange={(e) => update("site", e.target.value)}>
          <option value="">—</option>
          <option value="GRANDE_SYNTHE">GRANDE SYNTHE</option>
          <option value="LOON_PLAGE">LOON PLAGE</option>
        </select>
      </fieldset>

      <Section title="Information sur le chantier">
        <Row label="Nom du chantier" value={data.chantier.nom} onChange={(v) => update("chantier.nom", v)} />
        <Row label="Adresse" value={data.chantier.adresse} onChange={(v) => update("chantier.adresse", v)} />
        <Row label="CP" small value={data.chantier.cp} onChange={(v) => update("chantier.cp", v)} />
        <Row label="Commune" value={data.chantier.commune} onChange={(v) => update("chantier.commune", v)} />
        <Row label="Contact Chantier" value={data.chantier.contact} onChange={(v) => update("chantier.contact", v)} />
        <Row label="Email" value={data.chantier.email} onChange={(v) => update("chantier.email", v)} />
        <Row label="Tél." value={data.chantier.tel} onChange={(v) => update("chantier.tel", v)} />
      </Section>

      <Section title="Producteur de déchet (Maître d’ouvrage)">
        <PartyForm base="producteur" data={data} update={update} />
      </Section>

      <Section title="Demandeur (entreprise chargée des travaux)">
        <PartyForm base="demandeur" data={data} update={update} />
      </Section>

      <Section title="Informations sur les déchets">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Code déchets</th>
              <th style={th}>Désignation déchets</th>
              <th style={th}>Oui (x)</th>
              <th style={th}>Quantités estimée (en T)</th>
            </tr>
          </thead>
          <tbody>
            {data.dechets.map((d, idx) => (
              <tr key={d.code + idx}>
                <td style={td}><b>{d.code}</b></td>
                <td style={td}>{d.designation}</td>
                <td style={{ ...td, textAlign: "center" }}>
                  <input type="checkbox" checked={d.oui} onChange={() => toggleDechet(idx, "oui")} />
                </td>
                <td style={td}>
                  <input
                    value={d.quantite}
                    onChange={(e) => {
                      const v = e.target.value;
                      setData((prev) => {
                        const next = structuredClone(prev);
                        next.dechets[idx].quantite = v;
                        return next;
                      });
                    }}
                    style={{ width: "100%" }}
                    placeholder="Ex: 12"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section title="Informations transporteurs">
        <PartyForm base="transporteur" data={data} update={update} />
      </Section>

      <Section title="Travaux parcelles bâtiments">
        <label style={{ marginRight: 12 }}>
          <input
            type="radio"
            name="tpb"
            checked={data.travauxParcellesBatiments === true}
            onChange={() => update("travauxParcellesBatiments", true)}
          />{" "}
          Oui
        </label>
        <label>
          <input
            type="radio"
            name="tpb"
            checked={data.travauxParcellesBatiments === false}
            onChange={() => update("travauxParcellesBatiments", false)}
          />{" "}
          Non
        </label>
      </Section>

      <Section title="Analyse (OBLIGATOIRE pour les déblais bitumeux)">
        <label style={{ marginRight: 12 }}>
          <input
            type="radio"
            name="analyse"
            checked={data.analyse.fournie === true}
            onChange={() => update("analyse.fournie", true)}
          />{" "}
          Analyse fournie
        </label>
        <label>
          <input
            type="radio"
            name="analyse"
            checked={data.analyse.fournie === false}
            onChange={() => update("analyse.fournie", false)}
          />{" "}
          Non
        </label>

        <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
          <div style={{ flex: 1 }}>
            <label>Analyse du</label>
            <input style={{ width: "100%" }} value={data.analyse.date} onChange={(e) => update("analyse.date", e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label>Ref</label>
            <input style={{ width: "100%" }} value={data.analyse.ref} onChange={(e) => update("analyse.ref", e.target.value)} />
          </div>
        </div>
      </Section>

      <p style={{ color: "#666", marginTop: 18 }}>
        Note : le cadre “Acceptation préalable … (S.E.M)” est laissé saisissable dans le modèle mais normalement réservé.
      </p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <fieldset style={{ border: "1px solid #ddd", padding: 12, marginTop: 14 }}>
      <legend style={{ fontWeight: 700 }}>{title}</legend>
      <div style={{ display: "grid", gap: 10 }}>{children}</div>
    </fieldset>
  );
}

function Row({ label, value, onChange, small }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: small ? "120px 1fr" : "180px 1fr", gap: 10, alignItems: "center" }}>
      <label>{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function PartyForm({ base, data, update }) {
  const p = data[base];
  return (
    <>
      <Row label="Raison sociale" value={p.raisonSociale} onChange={(v) => update(`${base}.raisonSociale`, v)} />
      <Row label="N° de SIRET" value={p.siret} onChange={(v) => update(`${base}.siret`, v)} />
      <Row label="Adresse" value={p.adresse} onChange={(v) => update(`${base}.adresse`, v)} />
      <Row label="CP" small value={p.cp} onChange={(v) => update(`${base}.cp`, v)} />
      <Row label="Commune" value={p.commune} onChange={(v) => update(`${base}.commune`, v)} />
      <Row label="Personne à contacter" value={p.contact} onChange={(v) => update(`${base}.contact`, v)} />
      <Row label="Email" value={p.email} onChange={(v) => update(`${base}.email`, v)} />
      <Row label="Tél." value={p.tel} onChange={(v) => update(`${base}.tel`, v)} />
    </>
  );
}

const th = { border: "1px solid #ccc", padding: 8, textAlign: "left", background: "#fafafa" };
const td = { border: "1px solid #ccc", padding: 8, verticalAlign: "top" };
